import styled, { keyframes, css } from "styled-components";
import { GlassCard } from "../../components/ui/Glass";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const PageHeader = styled.div`
  position: relative;
  padding-top: calc(4rem + 60px);
  padding-bottom: ${({ theme }) => theme.spacing["3xl"]};
  background: ${({ theme }) => theme.colors.navy};
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        circle at 80% 20%,
        rgba(112, 141, 171, 0.18) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 10% 90%,
        rgba(201, 169, 110, 0.07) 0%,
        transparent 45%
      );
    pointer-events: none;
  }

  & > * {
    position: relative;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-top: calc(4rem + 50px);
  }
`;

export const HeaderContent = styled.div`
  max-width: 600px;
`;

export const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2.5rem, 5vw, ${({ theme }) => theme.fontSizes["6xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  letter-spacing: ${({ theme }) => theme.letterSpacings.tight};
`;

export const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: rgba(255, 255, 255, 0.55);
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

export const ConditionCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  max-width: 800px;
  margin: 0 auto;
`;

const variantFill = {
  success: "linear-gradient(120deg, #1f5a40 0%, #2d8460 55%, #3fb088 100%)",
  primary: "linear-gradient(120deg, #2c3f55 0%, #486682 55%, #708dab 100%)",
  gold: "linear-gradient(120deg, #4d3f24 0%, #8a7547 55%, #c9a96e 100%)",
};

const variantAccent = {
  success: "#3fb088",
  primary: "#708dab",
  gold: "#c9a96e",
};

export const ConditionCard = styled.button`
  position: relative;
  display: block;
  width: 100%;
  min-height: 240px;
  padding: 0;
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.normal};
  box-shadow:
    0 10px 28px rgba(26, 35, 50, 0.18),
    0 2px 8px rgba(26, 35, 50, 0.08);

  &:hover {
    transform: translateY(-3px);
    box-shadow:
      0 18px 40px rgba(26, 35, 50, 0.28),
      0 6px 16px rgba(26, 35, 50, 0.14);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 220px;
  }
`;

export const ConditionCardImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${({ $src }) => ($src ? `url(${$src})` : "none")};
  background: ${({ $src, $variant }) =>
    $src ? undefined : variantFill[$variant] || variantFill.primary};
  background-size: cover;
  background-position: center;
  transition: transform 700ms cubic-bezier(0.16, 1, 0.3, 1);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      100deg,
      rgba(15, 22, 36, 0.85) 0%,
      rgba(15, 22, 36, 0.65) 35%,
      rgba(15, 22, 36, 0.35) 70%,
      rgba(15, 22, 36, 0.2) 100%
    );
  }

  ${ConditionCard}:hover & {
    transform: scale(1.05);
  }
`;

export const ConditionCardInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  height: 100%;
  min-height: inherit;
  padding: ${({ theme }) => theme.spacing.xl};
  gap: ${({ theme }) => theme.spacing.md};
  border-left: 3px solid
    ${({ $variant }) => variantAccent[$variant] || variantAccent.primary};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

export const ConditionCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.md};
`;

export const ConditionCardCount = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.xs};
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);

  strong {
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: ${({ theme }) => theme.fontSizes.xl};
    font-weight: ${({ theme }) => theme.fontWeights.regular};
    color: ${({ theme }) => theme.colors.white};
    letter-spacing: -0.01em;
    line-height: 1;
  }
`;

export const ConditionCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 520px;
`;

export const ConditionCardTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.5rem, 2.4vw, ${({ theme }) => theme.fontSizes["3xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.white};
  letter-spacing: ${({ theme }) => theme.letterSpacings.tight};
  line-height: 1.15;
  margin: 0;
`;

export const ConditionCardDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: rgba(255, 255, 255, 0.78);
  line-height: ${({ theme }) => theme.lineHeights.normal};
`;

export const ConditionCardCTA = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.white};
  padding-bottom: 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.4);
  transition: gap ${({ theme }) => theme.transitions.fast};

  &::after {
    content: "→";
    font-size: 16px;
    line-height: 1;
    transition: transform ${({ theme }) => theme.transitions.fast};
  }

  ${ConditionCard}:hover &::after {
    transform: translateX(4px);
  }
`;

export const SelectedBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

export const SelectedBarText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};

  strong {
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

export const ShowAllBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};

  &::before {
    content: "×";
    font-size: 18px;
    line-height: 1;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

export const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  /* BackLink carries its own bottom margin; cancel it inside this row */
  > button {
    margin-bottom: 0;
  }
`;

export const FiltersBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: ${({ theme }) => theme.spacing.sm};

    /* Allow CustomSelect wrappers to shrink inside grid cells */
    > div {
      min-width: 0;
    }
  }
`;

export const SearchField = styled.div`
  position: relative;
  flex: 1 1 220px;
  min-width: 200px;
  max-width: 320px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-column: 1 / -1;
    max-width: none;
    min-width: 0;
  }

  svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.textLight};
    pointer-events: none;
  }

  input {
    width: 100%;
    padding: 12px 36px 12px 40px;
    font-size: ${({ theme }) => theme.fontSizes.md};
    background: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    outline: none;
    transition: all ${({ theme }) => theme.transitions.fast};
    color: ${({ theme }) => theme.colors.textPrimary};

    &::placeholder {
      color: ${({ theme }) => theme.colors.midGray};
    }

    &:hover {
      border-color: ${({ theme }) => theme.colors.midGray};
    }

    &:focus {
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 3px rgba(112, 141, 171, 0.12);
    }
  }

  button {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: ${({ theme }) => theme.colors.textLight};
    font-size: 16px;
    line-height: 1;

    &:hover {
      background: ${({ theme }) => theme.colors.lightGray};
      color: ${({ theme }) => theme.colors.textPrimary};
    }
  }
`;

export const ResultCount = styled.p`
  margin-left: auto;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};

  span {
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-column: 1 / -1;
    text-align: center;
    margin-left: unset;
    padding-top: ${({ theme }) => theme.spacing.xs};
  }
`;

export const ClearBtn = styled.button`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  padding: 10px 16px;
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-column: 1 / -1;
    justify-self: center;
    padding: 8px 16px;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const YachtCard = styled(GlassCard).attrs({ $variant: "light" })`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  box-shadow: ${({ theme }) => theme.shadows.md};
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;

  > div:last-child {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.white};
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow:
      0 16px 40px rgba(59, 84, 109, 0.18),
      0 4px 12px rgba(112, 141, 171, 0.12);
  }
`;

export const TagRow = styled.div`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing.md};
  left: ${({ theme }) => theme.spacing.md};
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  z-index: 1;
`;

export const PlaceholderImg = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.pearl} 0%,
    ${({ theme }) => theme.colors.lightGray} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.midGray};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

export const YachtBrand = styled.p`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const YachtName = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.fontSizes["2xl"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  transition: color ${({ theme }) => theme.transitions.fast};

  ${YachtCard}:hover & {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

export const Specs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  margin-top: auto;
  width: 100%;
`;

export const Spec = styled.div`
  /* When $truncate, share the row and clip overflow to a single line. */
  ${({ $truncate }) =>
    $truncate &&
    css`
      flex: 1 1 0;
      min-width: 0;
    `}

  span {
    display: block;
    font-family: ${({ theme }) => theme.fonts.caps};
    font-size: 10px;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.textLight};
    margin-bottom: 2px;
  }

  p {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    color: ${({ theme }) => theme.colors.textPrimary};

    ${({ $truncate }) =>
      $truncate &&
      css`
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `}
  }
`;

export const Price = styled.p`
  /* Always on its own row, right-aligned, so the specs above stay on one line */
  flex: 0 0 100%;
  text-align: right;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

export const EmptyState = styled.div`
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  grid-column: 1 / -1;
  padding: ${({ theme }) => `${theme.spacing["3xl"]}} ${theme.spacing["2xl"]}`};

  background: ${({ theme }) => theme.colors.white};
  border: 1px dashed ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.xl};

  svg {
    width: 28px;
    height: 28px;
    color: ${({ theme }) => theme.colors.primary};
    padding: 22px;
    box-sizing: content-box;
    background: ${({ theme }) => theme.colors.pearl};
    border-radius: ${({ theme }) => theme.borderRadius.full};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    box-shadow: 0 0 0 8px rgba(112, 141, 171, 0.05);
  }

  p {
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: ${({ theme }) => theme.fontSizes["2xl"]};
    font-weight: ${({ theme }) => theme.fontWeights.regular};
    color: ${({ theme }) => theme.colors.textPrimary};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }

  span {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    color: ${({ theme }) => theme.colors.textLight};
    max-width: 360px;
    line-height: ${({ theme }) => theme.lineHeights.relaxed};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

export const ClearFiltersBtn = styled.button`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.primary};
  padding: 12px 28px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(112, 141, 171, 0.3);
  }
`;

/* ── Curated "Available Now" sections ── */

export const GroupSection = styled.div`
  & + & {
    margin-top: ${({ theme }) => theme.spacing["3xl"]};
  }
`;

export const GroupHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

export const GroupHeading = styled.div`
  min-width: 0;
`;

export const GroupTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.5rem, 3vw, ${({ theme }) => theme.fontSizes["3xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.15;
`;

export const GroupSubtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.xs};
  max-width: 520px;
`;

export const GroupCount = styled.span`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textLight};
  flex-shrink: 0;
`;

export const Sentinel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing["2xl"]} 0
    ${({ theme }) => theme.spacing.lg};

  &::after {
    content: "";
    width: 24px;
    height: 24px;
    border: 2px solid ${({ theme }) => theme.colors.lightGray};
    border-top-color: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
  }
`;

export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: none;
  transition: color ${({ theme }) => theme.transitions.fast};

  &::before {
    content: "←";
    font-size: ${({ theme }) => theme.fontSizes.sm};
    transition: transform ${({ theme }) => theme.transitions.fast};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
  &:hover::before {
    transform: translateX(-3px);
  }
`;
